/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';

import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const TUNING_HISTORY_STATUS = Object.freeze({
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
  ABANDONED: 'abandoned',
});

const pidSideSchema = Yup.object().shape({
  kp: Yup.number().required('Kp is required'),
  tnSeconds: Yup.number().required('Tn is required'),
  tvSeconds: Yup.number().required('Tv is required'),
  tdSeconds: Yup.number().required('Td is required'),
  maxOvershoot: Yup.number().required('Max overshoot is required'),
});

export const tuningHistorySchema = Yup.object().shape({
  pressId: Yup.string().required('Press ID is required'),
  note: Yup.string().optional(),
  startTime: YupFirestoreTimestamp.required('Start time is required'),
  endTime: YupFirestoreTimestamp.when('status', {
    is: (status) => status !== TUNING_HISTORY_STATUS.IN_PROGRESS,
    then: (schema) => schema.required('End time is required'),
    otherwise: (schema) => schema.nullable().default(undefined),
  }),
  status: Yup.string()
    .oneOf(Object.values(TUNING_HISTORY_STATUS))
    .required('Status is required'),
  upper: pidSideSchema.when('status', {
    is: TUNING_HISTORY_STATUS.COMPLETE,
    then: (schema) => schema.required('Upper tuning values are required'),
    otherwise: (schema) => schema.nullable().default(undefined),
  }),
  lower: pidSideSchema.when('status', {
    is: TUNING_HISTORY_STATUS.COMPLETE,
    then: (schema) => schema.required('Lower tuning values are required'),
    otherwise: (schema) => schema.nullable().default(undefined),
  }),
});

/**
 * @typedef {Object} PidSide
 * @property {number} kp
 * @property {number} tnSeconds
 * @property {number} tvSeconds
 * @property {number} tdSeconds
 * @property {number} maxOvershoot
 *
 * @typedef {Object} TuningHistorySchema
 * @property {string} pressId
 * @property {string|null} [note]
 * @property {any} startTime
 * @property {any} endTime
 * @property {string} status
 * @property {PidSide} upper
 * @property {PidSide} lower
 *
 * @typedef {BaseDocData & TuningHistorySchema} TuningHistoryDocData
 */

export default class TuningHistory {
  /** @type {TuningHistoryDocData['validation']} */ validation = 'passed';
  /** @type {TuningHistoryDocData['id']} */ id;
  /** @type {TuningHistoryDocData['pressId']} */ pressId;
  /** @type {TuningHistoryDocData['note']} */ note;
  /** @type {TuningHistoryDocData['startTime']} */ startTime;
  /** @type {TuningHistoryDocData['endTime']} */ endTime;
  /** @type {TuningHistoryDocData['status']} */ status;
  /** @type {TuningHistoryDocData['upper']} */ upper;
  /** @type {TuningHistoryDocData['lower']} */ lower;

  /**
   * @param {TuningHistoryDocData} param0
   */
  constructor({
    id,
    pressId,
    note,
    startTime,
    endTime,
    status,
    upper,
    lower,
  }) {
    const normalizedStatus = status || (
      upper && lower ? TUNING_HISTORY_STATUS.COMPLETE : TUNING_HISTORY_STATUS.IN_PROGRESS
    );

    const validatedData = tuningHistorySchema.validateSync({
      pressId,
      note,
      startTime,
      endTime,
      status: normalizedStatus,
      upper,
      lower,
    });

    this.id = id || null;
    this.pressId = validatedData.pressId;
    this.note = validatedData.note;
    this.startTime = validatedData.startTime;
    this.endTime = validatedData.endTime;
    this.status = validatedData.status;
    this.upper = validatedData.upper;
    this.lower = validatedData.lower;
  }

  toFirestore() {
    return Object.fromEntries(Object.entries({
      pressId: this.pressId,
      note: this.note,
      startTime: this.startTime,
      endTime: this.endTime,
      status: this.status,
      upper: this.upper,
      lower: this.lower,
    }).filter(([, value]) => value !== undefined));
  }

  static fromFirestore(docData) {
    return new TuningHistory(docData);
  }
}
